import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { API } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, fecha, num, pct } from '../lib/format';
import type { InversionAcumulada, Lote, MesConYTD, PuntoEquilibrio } from '../api/types';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { IndicadoresFinancieros, distribucionDesdeLote } from '../components/IndicadoresFinancieros';
import { useParametros } from '../lib/finanzas';

export function DashboardPage() {
  const { tiene, session } = useAuth();
  const { margenObjetivo } = useParametros();

  const invQ = useQuery({
    queryKey: ['inversion-acumulada'],
    queryFn: () => API.get<InversionAcumulada>('/reportes/inversion-acumulada'),
    enabled: tiene('reportes:read'),
  });

  const saldoQ = useQuery({
    queryKey: ['saldo'],
    queryFn: () => API.get<{ saldo: number }>('/movimientos/saldo'),
    enabled: tiene('movimientos:read'),
  });

  const mesQ = useQuery({
    queryKey: ['reporte-mensual', new Date().getFullYear()],
    queryFn: () =>
      API.get<{ anio: number; meses: MesConYTD[] }>(
        `/reportes/mensual?anio=${new Date().getFullYear()}`,
      ),
    enabled: tiene('reportes:read'),
  });

  const lotesQ = useQuery({
    queryKey: ['ultimos-lotes'],
    queryFn: () =>
      API.get<{ items: Lote[]; agregados: { paquetes: number; ingreso: number; ganancia: number } }>(
        '/lotes?limit=5',
      ),
    enabled: tiene('lotes:read'),
  });

  const peQ = useQuery({
    queryKey: ['punto-equilibrio'],
    queryFn: () => API.get<PuntoEquilibrio>('/reportes/punto-equilibrio'),
    enabled: tiene('reportes:read'),
  });

  const mesActualNum = new Date().getMonth();
  const mesActual = mesQ.data?.meses?.[mesActualNum];
  const ultimoLote = lotesQ.data?.items?.[0];

  const lotesColumns: Column<Lote>[] = [
    {
      key: 'fecha',
      header: 'Fecha',
      mobile: 'primary',
      render: (l) => (
        <div>
          <div className="text-sm font-semibold">{fecha(l.fecha)}</div>
          <div className="text-xs text-slate-500">{num(l.paquetes, 0)} paquetes</div>
        </div>
      ),
    },
    {
      key: 'ingreso',
      header: 'Ingreso total',
      mobile: 'secondary',
      align: 'right',
      render: (l) => <span className="font-semibold text-emerald-700">{cop(Number(l.ingresoTotal))}</span>,
    },
    { key: 'paq', header: 'Paquetes', align: 'right', render: (l) => num(l.paquetes, 0) },
    { key: 'costo', header: 'Costo/paq', align: 'right', render: (l) => cop(Number(l.costoTotalPaq)) },
    { key: 'gan', header: 'Ganancia/paq', align: 'right', render: (l) => cop(Number(l.gananciaPaq)) },
    { key: 'margen', header: 'Margen', align: 'right', render: (l) => pct(Number(l.margenPct)) },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <header>
        <h1 className="text-xl sm:text-2xl font-semibold">Hola, {session?.usuario.nombre.split(' ')[0]} 👋</h1>
        <p className="text-sm text-slate-500">Resumen general del negocio.</p>
      </header>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <Card title="Saldo de caja" value={cop(saldoQ.data?.saldo)} hint="Aportes + ingresos - gastos; compras quedan en inventario" loading={saldoQ.isLoading} />
        <Card title="Total invertido" value={cop((invQ.data?.totalInversiones ?? 0) + (invQ.data?.totalAportes ?? 0))} hint={`Activos: ${cop(invQ.data?.totalInversiones ?? 0)}`} loading={invQ.isLoading} />
        <Card title="Depreciación mensual" value={cop(invQ.data?.depreciacionMensual)} hint="Costo fijo por paquete" loading={invQ.isLoading} />
        <Card title={`Utilidad ${new Date().getFullYear()} YTD`} value={cop(mesActual?.utilidadYTD)} hint={`Margen mes: ${pct(mesActual?.margenPct ?? 0)}`} loading={mesQ.isLoading} />
      </div>

      {tiene('reportes:read') && peQ.data && (
        <PuntoEquilibrioResumen pe={peQ.data} />
      )}

      {tiene('lotes:read') && ultimoLote && (
        <div className="card p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 mb-3">
            <h2 className="font-semibold">
              Último lote <span className="text-slate-500 font-normal text-sm">· {fecha(ultimoLote.fecha)}</span>
            </h2>
            <span className="text-xs text-slate-500">
              {num(ultimoLote.paquetes, 0)} paquetes · {num(Number(ultimoLote.kilosMaiz), 2)} kg maíz
            </span>
          </div>
          <IndicadoresFinancieros
            lote={ultimoLote}
            margenObjetivo={margenObjetivo}
            distribucion={distribucionDesdeLote(ultimoLote)}
          />
        </div>
      )}

      {tiene('lotes:read') && (
        <div className="card">
          <div className="p-3 sm:p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
            <h2 className="font-semibold">Últimos lotes</h2>
            <span className="text-xs text-slate-500">
              {lotesQ.data?.agregados?.paquetes ?? 0} paquetes · {cop(lotesQ.data?.agregados?.ingreso)} ingresos
            </span>
          </div>
          <ResponsiveTable<Lote>
            columns={lotesColumns}
            rows={lotesQ.data?.items}
            rowKey={(l) => l.id}
            loading={lotesQ.isLoading}
            empty="Sin lotes aún."
          />
        </div>
      )}
    </div>
  );
}

function Card({ title, value, hint, loading }: { title: string; value: string; hint?: string; loading?: boolean }) {
  return (
    <div className="card p-3 sm:p-4">
      <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 leading-tight">{title}</div>
      <div className="text-lg sm:text-2xl font-semibold mt-1 break-words">
        {loading ? <span className="text-slate-300">…</span> : value}
      </div>
      {hint && <div className="text-[11px] sm:text-xs text-slate-500 mt-1 leading-tight">{hint}</div>}
    </div>
  );
}

function PuntoEquilibrioResumen({ pe }: { pe: PuntoEquilibrio }) {
  const imposible = pe.paquetesEquilibrioMes === null;
  const objetivo = pe.paquetesEquilibrioMes ?? 0;
  const estimado = pe.paquetesEstimadosMes;
  const cobertura =
    !imposible && objetivo > 0 ? Math.min(100, (estimado / objetivo) * 100) : 0;

  return (
    <div className="card p-3 sm:p-4">
      <div className="flex justify-between items-start gap-2 mb-2">
        <div>
          <h2 className="font-semibold">Punto de equilibrio</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Paquetes que necesitas vender al mes para cubrir los costos fijos.
          </p>
        </div>
        <Link
          to="/punto-equilibrio"
          className="text-xs font-medium text-slate-700 hover:text-slate-900 whitespace-nowrap"
        >
          Ver detalle →
        </Link>
      </div>

      {imposible ? (
        <p className="text-sm text-red-600">
          El precio de venta no cubre el costo variable. Revisa precios o costos.
        </p>
      ) : (
        <>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-2xl sm:text-3xl font-semibold">{num(objetivo, 0)}</span>
            <span className="text-xs text-slate-500">paquetes/mes para empatar</span>
            <span
              className={`text-xs ${estimado >= objetivo ? 'text-emerald-700' : 'text-red-600'}`}
            >
              {estimado >= objetivo
                ? `Estimado ${num(estimado, 0)} (sobran ${num(estimado - objetivo, 0)})`
                : `Estimado ${num(estimado, 0)} (faltan ${num(objetivo - estimado, 0)})`}
            </span>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Avance hacia el equilibrio</span>
              <span>{pct(cobertura / 100)}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${estimado >= objetivo ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${cobertura}%` }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
